import { CommonModule } from '@angular/common'
import { Component, HostListener, Inject, inject, OnInit, Optional, signal } from '@angular/core'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatIconModule } from '@angular/material/icon'
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { Router, ActivatedRoute } from '@angular/router'
import { NgxMaskDirective } from 'ngx-mask'
import { isMobile, nameIsValid, normalizePhone, normalizeEmail } from '@monorepo-channels/shared/util-helpers'
import {
	Observable,
	Subject,
	startWith,
	switchMap,
	timer,
	map,
	takeWhile,
	catchError,
	of,
	finalize,
	take,
	debounceTime,
	filter,
	distinctUntilChanged,
} from 'rxjs'
import { GenericErrorComponent } from '../simple-bottom-sheets/generic-error.component'
import { AuthService } from './sign-up.service'
import { CreateSignupCodeDto } from './sign-up.model'
import { ClientActions, AuthService as GlobalAuthService } from '@monorepo-channels/channels/domain'
import { Store } from '@ngrx/store'

export function countdownFrom(start: number): Observable<number> {
	return timer(0, 1000).pipe(
		map(index => start - index),
		takeWhile(value => value >= 0, true) // Emitir até 0 (inclusive)
	)
}

@Component({
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatBottomSheetModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		NgxMaskDirective,
		// RouterLink,
	],
	providers: [AuthService],
	selector: 'feature-porto-bank-sign-up',
	templateUrl: './sign-up.component.html',
	styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit {
	private fb = inject(FormBuilder)
	private router = inject(Router)
	private route = inject(ActivatedRoute)
	private authService = inject(AuthService)
	private bottomSheet = inject(MatBottomSheet)
	private dialog = inject(MatDialog)
	private globalAuthService = inject(GlobalAuthService)
	private store = inject(Store)
	private dialogRef = inject(MatDialogRef<SignUpComponent>, { optional: true })

	isMobile = isMobile()

	constructor(
		@Optional()
		@Inject(MAT_DIALOG_DATA)
		private dialogData: { message: string; login?: boolean } | null
	) {
		if (this.dialogData?.login) {
			this.mode.set('login')
		}
		// Verificar queryParam 'login' e mudar automaticamente para modo login
		this.route.queryParams.pipe(take(1)).subscribe(params => {
			if (params['login'] !== undefined) {
				this.mode.set('login')
			}
		})
	}

	@HostListener('window:resize')
	onResize() {
		this.isMobile = isMobile()
	}

	// Estado do fluxo: initial_form -> verify_otp -> success
	public step = signal<'initial_form' | 'verify_otp' | 'success'>('initial_form')
	public mode = signal<'signup' | 'login'>('signup') // Modo: cadastro ou login
	public isLoading = signal(false)
	public isLoadingResend = signal(false)

	// Client ID retornado pela API após cadastro/login
	private clientId: string | null = null

	// Formulário de cadastro (sign-up)
	public signUpForm = this.fb.nonNullable.group({
		name: ['', [Validators.required, nameIsValid]],
		phone: ['', [Validators.required, Validators.minLength(11)]],
		cpf: ['', [Validators.required, Validators.minLength(11)]],
		email: ['', [Validators.required, Validators.email]],
		bin: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
	})

	// Formulário de login (apenas CPF)
	public loginForm = this.fb.nonNullable.group({
		cpf: ['', [Validators.required, Validators.minLength(11)]],
	})

	// Formulário OTP (4 dígitos)
	public otpForm = this.fb.nonNullable.group({
		otp0: ['', [Validators.required]],
		otp1: ['', [Validators.required]],
		otp2: ['', [Validators.required]],
		otp3: ['', [Validators.required]],
	})

	// Estados de foco dos campos
	public focusStates = {
		name: false,
		phone: false,
		cpf: false,
		email: false,
		bin: false,
	}

	// Countdown para reenvio de código
	private readonly resendSubject = new Subject<void>()
	readonly resend$ = this.resendSubject.asObservable()
	readonly countdown$ = this.resend$.pipe(
		startWith(1),
		switchMap(() => countdownFrom(30))
	)

	// Telefone mascarado para exibição
	public maskedPhone = signal('')
	public maskedEmail = signal('')

	ngOnInit() {
		this.loginForm.controls.cpf.valueChanges
			.pipe(
				debounceTime(300),
				map(cpf => cpf?.replace(/\D/g, '')),
				filter(cpf => cpf?.length === 11),
				distinctUntilChanged(),
				switchMap(cpf => {
					this.isLoading.set(true)

					return this.authService.loginCPF({ cpf }).pipe(
						finalize(() => this.isLoading.set(false)),
						catchError(this.handleError)
					)
				})
			)
			.subscribe(response => {
				if (response?.clientId) {
					this.clientId = response.clientId
					this.maskedEmail.set(`${response.email.slice(0, 3)}*****${response.email.slice(-4)}`)
					this.step.set('verify_otp')
					this.resendSubject.next()
				}
			})
	}

	// Métodos do formulário principal
	onSubmitSignUpForm() {
		if (this.signUpForm.invalid) {
			this.signUpForm.markAllAsTouched()
			return
		}

		this.isLoading.set(true)

		// Normalizar dados e preparar DTO
		const rawFormData = this.signUpForm.getRawValue()
		const dto: CreateSignupCodeDto = {
			name: rawFormData.name,
			phone: normalizePhone(rawFormData.phone),
			email: normalizeEmail(rawFormData.email),
			cpf: rawFormData.cpf,
			bin: Number(rawFormData.bin), // Converter para number
		}

		// Armazenar telefone mascarado para exibição
		this.maskedPhone.set(`(${dto.phone.slice(0, 2)})*****${dto.phone.slice(-4)}`)
		this.maskedEmail.set(`${dto.email.slice(0, 3)}*****${dto.email.slice(-4)}`)
		// Chamar API para criar código de cadastro
		this.authService
			.createSignupCode(dto)
			.pipe(
				take(1),
				finalize(() => this.isLoading.set(false)),
				catchError(this.handleError)
			)
			.subscribe({
				next: response => {
					if (response?.clientId) {
						this.clientId = response.clientId
						this.step.set('verify_otp')
						this.resendSubject.next()
					}
				},
			})
	}

	// Método do formulário de login (login é automático ao completar o CPF)
	onSubmitLoginForm() {
		if (this.loginForm.invalid) {
			this.loginForm.markAllAsTouched()
		}
	}

	// Métodos para alternar entre signup e login
	switchToLogin() {
		this.mode.set('login')
		this.step.set('initial_form')
		this.loginForm.reset()
	}

	switchToSignup() {
		this.mode.set('signup')
		this.step.set('initial_form')
		this.signUpForm.reset()
	}

	// Métodos do OTP
	onKeyUp(event: KeyboardEvent) {
		const currentInput = event.target as HTMLInputElement
		if (currentInput.value.length === 1) {
			const nextInput = currentInput.nextElementSibling as HTMLInputElement
			if (nextInput) {
				nextInput.focus()
			}
		}
	}

	onKeyDown(event: KeyboardEvent, index: number) {
		const currentInput = event.target as HTMLInputElement
		if (event.key === 'Backspace' && currentInput.value.length === 0 && index > 0) {
			const previousInput = currentInput.previousElementSibling as HTMLInputElement
			if (previousInput) {
				previousInput.focus()
			}
		}
	}

	onPaste(event: ClipboardEvent) {
		event.preventDefault()
		let pastedData = event.clipboardData?.getData('text/plain')
		pastedData = pastedData?.trim()
		if (pastedData && pastedData.length >= 4) {
			this.otpForm.patchValue({
				otp0: pastedData[0] || '',
				otp1: pastedData[1] || '',
				otp2: pastedData[2] || '',
				otp3: pastedData[3] || '',
			})
		}
	}

	onSubmitOtp() {
		if (this.otpForm.valid) {
			this.isLoading.set(true)

			const otp = Object.values(this.otpForm.getRawValue()).join('')

			this.authService
				.confirmSignupCode({
					clientId: this.clientId as string,
					registrationCode: otp.toLocaleUpperCase(),
				})
				.pipe(
					take(1),
					finalize(() => this.isLoading.set(false))
				)
				.subscribe({
					next: response => {
						if (response?.tokens) {
							localStorage.setItem('clientId', this.clientId as string)
							this.globalAuthService.setTokens({
								accessToken: response.tokens.accessToken,
								refreshToken: response.tokens.refreshToken,
							})

							this.dialogRef?.close()
							this.store.dispatch(ClientActions.loadClient({ id: this.clientId as string }))
							this.router.navigateByUrl('/')
						} else {
							this.handleError({
								error: { message: 'Falha ao autenticar. Tente novamente.' },
							})
						}
					},
					error: error => {
						this.handleError(error)
					},
				})
		} else {
			this.otpForm.markAllAsTouched()
		}
	}

	sendValidationCode() {
		this.isLoadingResend.set(true)

		this.authService
			.resendSignupCode({ clientId: this.clientId as string, type: 'email' })
			.pipe(
				take(1),
				finalize(() => this.isLoadingResend.set(false)),
				catchError(this.handleError)
			)
			.subscribe({
				next: response => {
					if (response?.success) {
						this.resendSubject.next()
					}
				},
			})
	}

	resendCode() {
		this.sendValidationCode()
	}

	onViewRestaurants() {
		this.router.navigate(['/'])
	}

	// Error Handler
	private handleError = (error: { error: { message: string } }) => {
		const translatedMessage = this.translateBackendError(
			error?.error?.message || 'Ocorreu um erro. Tente novamente.'
		)
		this.dialog.open(GenericErrorComponent, {
			data: {
				error: translatedMessage,
				title: 'Erro',
				btnMessage: 'Fechar',
			},
			width: '400px',
		})
		return of(null)
	}

	private translateBackendError(msg: string): string {
		if (!msg) return 'Ocorreu um erro inesperado.'

		const normalized = msg?.[0].toLowerCase()

		if (normalized.includes('must be an email')) return 'E-mail inválido.'
		if (normalized.includes('invalid cpf')) return 'CPF inválido.'
		if (normalized.includes('cpf must be')) return 'CPF inválido.'
		if (normalized.includes('phone must be')) return 'Telefone inválido.'
		if (normalized.includes('bin must be')) return 'BIN inválido.'

		// fallback
		return msg
	}

	// Helpers
	isFieldInvalid(fieldName: keyof typeof this.focusStates): boolean {
		const field = this.signUpForm.get(fieldName)
		// Mostrar erro apenas quando sair do campo (blur/touched) E tiver digitado algo (dirty)
		return !!(field && field.invalid && field.dirty && field.touched)
	}

	toggleFocus(fieldName: keyof typeof this.focusStates, focused: boolean) {
		this.focusStates[fieldName] = focused
	}
}
